"use strict"

import React, { forwardRef } from "react"

import ObservationPlot from "../../plotting/ObservationPlot"
import { ObservationKey } from "../../plotting/ObservationPlot/ObservationKey"
import ObsMultiTagKey from "../../plotting/GraphKeys/ObsMultiTagKey"
import { ObservationPlotBaton } from "./ObservationPlotBaton"
import { exportCSVFromArray, exportCommentsObject } from "../../data_export/ObservationExporter"
import { exportObservationsObject } from "../../../../components/graphing/data_export/ObservationExporter"
import { isLTPM } from "../../../../components/shared_components_registration"
import { isHomeDashPlot } from "../../resources/utils"

const registration = {
  select_tags_label: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.select.tags.to.compare" }) : "Select Tags to Compare",
}

export const getCommentDataForRows = (
  actionItems,
  run,
  timeExtractor,
  minTime,
  maxTime,
  tzString,
  intl,
  multi = false,
) => {
  return exportCommentsObject(
    actionItems,
    timeExtractor,
    minTime,
    maxTime,
    run.observationTypesFlat,
    tzString,
    intl,
    multi,
  )
}

export const getObservationPlotData = (config, key, run, force_reload = false, is_first_run = false) => {
  // Abort if we don't have the necessary data load functions in the run state
  if (!run.observationTypesFlat || !run.requestObservations) return

  for (const obs of config[key] || []) {
    if (
      run.observationTypesFlat[obs.tag] &&
      (!obs.smp_src || run.observationTypesFlat[obs.tag]?.["types"].includes(obs.smp_src))
    ) {
      if (isHomeDashPlot(config) && config.special === "site-wide") {
        // Loads SiteObservation data
        is_first_run &&
          run.requestSiteObservations &&
          run.requestSiteObservations(obs.tag, obs.units, force_reload)
      } else {
        if (obs.smp_src) {
          // Loads SampleObservation data
          run.requestSmpObservations &&
            run.requestSmpObservations(obs.tag, obs.units, obs.smp_src, force_reload)
        } else {
          // Loads Observation data
          run.requestObservations && run.requestObservations(obs.tag, obs.units, force_reload)
        }
      }
    }
  }
}

export const ObservationKeyWrapper = (props) => {
  // allowTagSelection is only True on LanzaLake Run Search page
  const { graphKey, download, trendEditKey, allowTagSelection = true } = props

  return (
    <>
      {!download &&
        allowTagSelection &&
        graphKey &&
        (!graphKey?.special || (graphKey.special === "multi" && !isHomeDashPlot(graphKey))) && (
          <ObsMultiTagKey {...props} />
        )}
      <ObservationKey {...props} observations={graphKey?.[trendEditKey]} tag_category={trendEditKey} />
    </>
  )
}

export const crossPlotProps = (plotPropsArray) => ({
  queryTags: (plotPropsArray[0] || {}).queryTags,
  observationTypesArray: plotPropsArray.map((plotProps) => plotProps.observationTypes || {}),
  sample_types:
    (plotPropsArray.length > 0 && [
      ...new Set(
        plotPropsArray.map((plot_prop) => plot_prop.sample_types || []).reduce((arr, val) => arr.concat(val)),
      ),
    ]) ||
    [],
})

const ObservationPlotFactory = (config) => ({
  Component: forwardRef((props, ref) => {
    if (config.observations.length === 0) {
      return (
        <div style={{ height: "220px", margin: "25px" }}>
          <span>
            <i>{registration.select_tags_label(props.intl)}</i>
          </span>
        </div>
      )
    }

    return <ObservationPlot ref={ref} {...props} layers={props.graphKey} />
  }),

  Key: (props) => <ObservationKeyWrapper trendEditKey={"observations"} {...props} />,

  makePlotBaton: () => {
    return new ObservationPlotBaton(config)
  },

  getCrossPlotProps: (plotPropsArray = []) => crossPlotProps(plotPropsArray),

  ensureDataRequested: (run, force_reload = false, is_first_run = false) =>
    getObservationPlotData(config, "observations", run, force_reload, is_first_run),

  getExportCSVName: (exportBaseName) => {
    return `${exportBaseName}_${(config.title || "unknown").replace(/[\s:]/g, "_")}.csv`
  },

  getCommentDataForRows: getCommentDataForRows,

  getDataForRows: (run, timeExtractor, minTime, maxTime, viewerState, zoomRanges, tzString, intl = null) => {
    return exportObservationsObject(
      timeExtractor,
      minTime,
      maxTime,
      run,
      config.observations,
      intl,
      config,
      tzString,
    )
  },

  makeExportCSV: (data) => exportCSVFromArray(data),
})

export default ObservationPlotFactory
